import http from '../../shared/http.js';
import { initFormModalLinks } from '../../shared/formModal.js';
import { withFlash } from '../../shared/flash.js';
import { hideLoading, notifyError, notifySuccess, showLoading } from '../../shared/notify.js';

// The Arbeitsformen, editable straight from the detail page - the same control
// kundeDetail.js gives the Vermittlungsarten, and deliberately the same shape.
// Each box saves on change rather than through a submit button: there is nothing
// to fill in alongside it and nothing to review before sending.
//
// One difference from the customer's pair, and it is the reason this is not a
// copy: those are two independent booleans, where this is one field holding a
// set. So the whole set is sent on every change - a PATCH with one value would
// replace the array rather than add to it - and the last box cannot be unticked,
// because a job with no Arbeitsform is a job the schema refuses. Catching that
// here rather than letting the API answer 400 keeps the box from flickering off
// and back for a rule we already know.
function initArbeitsformen() {
  const container = document.getElementById('job-arbeitsformen');
  if (!container) return;

  const { jobId } = container.dataset;
  const boxen = Array.from(container.querySelectorAll('input[type="checkbox"]'));

  const gewaehlt = () => boxen.filter((box) => box.checked).map((box) => box.value);

  boxen.forEach((box) => {
    box.addEventListener('change', async () => {
      const werte = gewaehlt();

      // Unticking the last one. Put back before anything is sent: the field is
      // required, so this is not a failure to report but an edit that cannot be
      // made.
      if (werte.length === 0) {
        box.checked = true;
        notifyError('Mindestens eine Arbeitsform muss gewählt bleiben.');
        return;
      }

      // The whole group goes dead for the round trip rather than just the box
      // that changed: a second click on a *different* box would send a set built
      // from the DOM mid-flight, and the two requests could land out of order.
      boxen.forEach((other) => {
        other.disabled = true;
      });

      try {
        await http.patch(`/jobs/${jobId}`, { arbeitsform: werte });
        notifySuccess('Arbeitsform wurde gespeichert.');
      } catch (err) {
        box.checked = !box.checked;
        notifyError(
          err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.'
        );
      } finally {
        boxen.forEach((other) => {
          other.disabled = false;
        });
      }
    });
  });
}

// The status, changed on the funnel that displays it: every stage is a button,
// so a move is one click on the stage somebody means rather than two on a picker
// that hides the path. Everything goes through /jobs/:id/status rather than a
// plain PATCH, because a status change is the one edit that writes a Verlauf
// entry: the endpoint is what guarantees it.
//
// Picking an ordinary status saves straight away. Picking Verloren opens the
// dialog instead: the schema refuses that status without a Verlustgrund, so a
// bare pick could only ever come back a 400. Exactly the split the customer's
// Blacklist makes, and the reason this is not simply a loop over the stages.
function initStatusFeld() {
  const funnel = document.getElementById('job-status-funnel');
  if (!funnel) return;

  const { jobId } = funnel.dataset;
  const modal = document.getElementById('job-verlust-modal');
  const modalForm = document.getElementById('job-verlust-form');

  // The status the record actually carries, so a click on the stage the job is
  // already in can be recognised and ignored.
  const aktuell = funnel.dataset.status;

  // A status change reloads: it redraws the funnel, may add or remove the
  // won/lost banner above the data, moves the Liegezeit line and always adds an
  // entry to the Verlauf column on the right. Keeping four regions in step with
  // the server by hand would be the slow way to be wrong.
  const speichern = async (data, ladetext) => {
    showLoading(ladetext);
    try {
      await http.patch(`/jobs/${jobId}/status`, data);
      window.location.href = withFlash(`/jobs/${jobId}`, 'job-status-geaendert');
    } catch (err) {
      hideLoading();
      notifyError(
        err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.'
      );
    }
  };

  // One handler on the chain rather than one per stage: the stages are rendered
  // by the server and there is nothing to rebind if the page ever redraws them.
  funnel.addEventListener('click', (event) => {
    const stufe = event.target.closest('[data-status]');
    if (!stufe || stufe === funnel) return;

    const gewaehlt = stufe.dataset.status;

    // Clicking the stage the job is already in is not a change; the endpoint
    // would answer it as a no-op, so it is not worth the trip.
    if (gewaehlt === aktuell) return;

    if (gewaehlt === 'closed_lost') {
      // The dialog carries the reason and the remarks, so the click opens it
      // rather than sending a request the schema would refuse.
      modal.showModal();
      return;
    }

    speichern({ status: gewaehlt }, 'Status wird gesetzt …');
  });

  if (!modalForm) return;

  modalForm.querySelector('[data-action="abbrechen"]')?.addEventListener('click', () => {
    modal.close();
  });

  modalForm.addEventListener('submit', (event) => {
    event.preventDefault();

    const grund = modalForm.querySelector('#verlust-grund').value;
    const verlustBemerkung = modalForm.querySelector('#verlust-bemerkung').value.trim();
    const bemerkung = modalForm.querySelector('#verlust-notiz').value.trim();

    // Required by the schema, so it is caught here rather than sent to be
    // refused - the German message is the same one the field would produce.
    if (!grund) return notifyError('Bitte wählen Sie einen Verlustgrund.');

    const data = { status: 'closed_lost', verlustgrund: grund };
    // The structured reason that stays on the record, and the note that becomes
    // a Verlauf entry. Two different things, which is why the dialog asks twice.
    if (verlustBemerkung) data.verlustBemerkung = verlustBemerkung;
    if (bemerkung) data.bemerkung = bemerkung;

    modal.close();
    speichern(data, 'Job wird als verloren markiert …');
  });
}

// The job's detail page: the status funnel and the header's Bearbeiten button.
export function initJobDetail() {
  // 'Bearbeiten' opens the job's form in the shared dialog rather than
  // navigating to it. A save reloads, since the page is composed server-side.
  //
  // Anchored on /jobs/…/edit specifically: the page links to the customer and
  // will link to candidate proposals, and neither belongs to this dialog.
  initFormModalLinks({
    selector: 'a[href^="/jobs/"][href$="/edit"]',
    onSaved: () => window.location.reload()
  });

  initStatusFeld();
  initArbeitsformen();
}
